// OrgChartControls.jsx
// react
import { useState } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import CloseFullscreenRoundedIcon from '@mui/icons-material/CloseFullscreenRounded';
import FilterAltRoundedIcon from '@mui/icons-material/FilterAltRounded';
import FitScreenRoundedIcon from '@mui/icons-material/FitScreenRounded';
import KeyboardTabRoundedIcon from '@mui/icons-material/KeyboardTabRounded';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import OpenWithRoundedIcon from '@mui/icons-material/OpenWithRounded';
import ZoomInRoundedIcon from '@mui/icons-material/ZoomInRounded';
import ZoomOutRoundedIcon from '@mui/icons-material/ZoomOutRounded';

// material ui
import {
  Box,
  ClickAwayListener,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Tooltip,
} from '@mui/material';

/**
 * Standalone group of tree control icons (zoom, filter, fit, expand/collapse).
 * Renders a compact "more" menu on small screens and a full row of icon
 * buttons on larger screens.
 */
const OrgChartControls = ({
  isSmallScreen,
  isPortfolio,
  zoomIn,
  zoomOut,
  onOpenFilterDialog,
  fitToScreen,
  expandAllNodes,
  collapseAllNodes,
  collapseOneLevel,
  expandOneLevel,
}) => {
  const { t } = useTranslation('energyxplorer-tree-tab');

  // State for controls menu popper
  const [controlsAnchorEl, setControlsAnchorEl] = useState(null);
  const controlsOpen = Boolean(controlsAnchorEl);

  if (isSmallScreen) {
    return (
      <>
        <Tooltip title={t('tree_actions.controls')} placement="bottom">
          <IconButton
            onClick={(e) => setControlsAnchorEl(e.currentTarget)}
            data-testid="orgchart-controls-menu"
            sx={{
              backgroundColor: 'background.paper',
              borderRadius: '6px',
              border: '1px solid',
              borderColor: 'divider',
              p: 1.1,
            }}
          >
            <MoreVertRoundedIcon />
          </IconButton>
        </Tooltip>
        {controlsOpen && (
          <ClickAwayListener onClickAway={() => setControlsAnchorEl(null)}>
            <Popper
              open={controlsOpen}
              anchorEl={controlsAnchorEl}
              placement="bottom-end"
              sx={{ zIndex: 1300 }}
            >
              <Paper
                elevation={8}
                sx={{
                  mt: 1,
                  borderRadius: '6px',
                  minWidth: 200,
                }}
              >
                <MenuList>
                  <MenuItem
                    onClick={() => {
                      zoomIn();
                      setControlsAnchorEl(null);
                    }}
                  >
                    <ListItemIcon>
                      <ZoomInRoundedIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{t('tree_actions.zoom_in')}</ListItemText>
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      zoomOut();
                      setControlsAnchorEl(null);
                    }}
                  >
                    <ListItemIcon>
                      <ZoomOutRoundedIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{t('tree_actions.zoom_out')}</ListItemText>
                  </MenuItem>
                  {!isPortfolio && (
                    <MenuItem
                      onClick={() => {
                        onOpenFilterDialog?.();
                        setControlsAnchorEl(null);
                      }}
                    >
                      <ListItemIcon>
                        <FilterAltRoundedIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>
                        {t('tree_actions.filter_nodes', {
                          ns: 'energyxplorer-tree-tab',
                        })}
                      </ListItemText>
                    </MenuItem>
                  )}
                  <Divider />
                  <MenuItem
                    onClick={() => {
                      fitToScreen();
                      setControlsAnchorEl(null);
                    }}
                  >
                    <ListItemIcon>
                      <FitScreenRoundedIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{t('tree_actions.fit_tree')}</ListItemText>
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      expandAllNodes();
                      setControlsAnchorEl(null);
                    }}
                  >
                    <ListItemIcon>
                      <OpenWithRoundedIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{t('tree_actions.expand_all')}</ListItemText>
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      collapseAllNodes();
                      setControlsAnchorEl(null);
                    }}
                  >
                    <ListItemIcon>
                      <CloseFullscreenRoundedIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>
                      {t('tree_actions.collapse_all')}
                    </ListItemText>
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      expandOneLevel();
                      setControlsAnchorEl(null);
                    }}
                  >
                    <ListItemIcon>
                      <KeyboardTabRoundedIcon
                        fontSize="small"
                        sx={{ transform: 'rotate(90deg)' }}
                      />
                    </ListItemIcon>
                    <ListItemText>{t('tree_actions.expand_one')}</ListItemText>
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      collapseOneLevel();
                      setControlsAnchorEl(null);
                    }}
                  >
                    <ListItemIcon>
                      <KeyboardTabRoundedIcon
                        fontSize="small"
                        sx={{ transform: 'rotate(-90deg)' }}
                      />
                    </ListItemIcon>
                    <ListItemText>
                      {t('tree_actions.collapse_one')}
                    </ListItemText>
                  </MenuItem>
                </MenuList>
              </Paper>
            </Popper>
          </ClickAwayListener>
        )}
      </>
    );
  }

  // Full view: All buttons visible on larger screens
  return (
    <>
      <Tooltip title={t('tree_actions.zoom_in')} placement="bottom">
        <IconButton
          onClick={zoomIn}
          data-testid="orgchart-zoom-in"
          sx={{
            borderRadius: '6px',
          }}
        >
          <ZoomInRoundedIcon />
        </IconButton>
      </Tooltip>

      <Tooltip title={t('tree_actions.zoom_out')} placement="bottom">
        <IconButton
          onClick={zoomOut}
          data-testid="orgchart-zoom-out"
          sx={{
            borderRadius: '6px',
          }}
        >
          <ZoomOutRoundedIcon />
        </IconButton>
      </Tooltip>

      {!isPortfolio && (
        <Tooltip
          title={t('tree_actions.filter_nodes', {
            ns: 'energyxplorer-tree-tab',
          })}
          placement="bottom"
        >
          <IconButton
            onClick={() => onOpenFilterDialog?.()}
            variant="outlined"
            data-testid="orgchart-filter-button"
            sx={{
              borderRadius: '6px',
            }}
          >
            <FilterAltRoundedIcon />
          </IconButton>
        </Tooltip>
      )}

      <Box
        data-testid="orgchart-expansion-control"
        sx={{
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Tooltip title={t('tree_actions.fit_tree')} placement="bottom">
          <IconButton
            onClick={fitToScreen}
            data-testid="orgchart-fit-tree"
            sx={{ borderRadius: '6px' }}
          >
            <FitScreenRoundedIcon />
          </IconButton>
        </Tooltip>

        <Tooltip title={t('tree_actions.expand_all')} placement="bottom">
          <IconButton
            onClick={expandAllNodes}
            data-testid="orgchart-expand-all"
            sx={{ borderRadius: '6px' }}
          >
            <OpenWithRoundedIcon />
          </IconButton>
        </Tooltip>

        <Tooltip title={t('tree_actions.collapse_all')} placement="bottom">
          <IconButton
            onClick={collapseAllNodes}
            data-testid="orgchart-collapse-all"
            sx={{ borderRadius: '6px' }}
          >
            <CloseFullscreenRoundedIcon />
          </IconButton>
        </Tooltip>

        <Tooltip title={t('tree_actions.collapse_one')} placement="bottom">
          <IconButton
            onClick={collapseOneLevel}
            data-testid="orgchart-collapse-one"
            sx={{ borderRadius: '6px' }}
          >
            <KeyboardTabRoundedIcon sx={{ transform: 'rotate(-90deg)' }} />
          </IconButton>
        </Tooltip>

        <Tooltip title={t('tree_actions.expand_one')} placement="bottom">
          <IconButton
            onClick={expandOneLevel}
            data-testid="orgchart-expand-one"
            sx={{ borderRadius: '6px' }}
          >
            <KeyboardTabRoundedIcon sx={{ transform: 'rotate(90deg)' }} />
          </IconButton>
        </Tooltip>
      </Box>
    </>
  );
};

export default OrgChartControls;
